import React, { PropsWithChildren } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import trimbleLogo from '../../assets/trimble-logo.svg';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import trimbleLogoWhite from '../../assets/trimble-logo-white.png';
import { useAuth } from '@trimble-oss/trimble-id-react';
import Grid from '@/components/Grid/Grid';
import Avatar from '@/components/Avatar/Avatar';
import Menu from '@/components/Menu/Menu';
import MenuItem from '@/components/MenuItem/MenuItem';
import GridSpacingLayout from '@/components/GridSpacingLayout/GridSpacingLayout';
import Box from '@/components/Box/Box';
import { useTranslation } from 'react-i18next';
import I18N_RESOURCE from '@/i18n/template';
import AccountNameText from '@/stand-alone-app/TrimbleSiteLayout/AccountNameText/AccountNameText';

const styles = {
  root: {
    width: '100%',
    minHeight: '100vh',
    display: 'flex' as const,
    flexDirection: 'column' as const,
  },
  header: {},
  topBar: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    maxWidth: '100%',
    paddingTop: '8px',
    paddingBottom: '8px',
    flexShrink: '0',
    justifyContent: 'space-between',
    background: '#FFF',
  },
  topBarLinks: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: '3rem',
  },
  topBarLink: {
    color: '#0063A3',
    textDecoration: 'none',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 'normal',
  },
  subheader: {
    display: 'flex',
    flexDirection: 'column' as const,
    maxWidth: '100%',
    paddingTop: '12px',
    paddingBottom: '12px',
    flexShrink: '0',
    background: '#252A2E',
    color: '#FFFFFF',
  },
  footer: { width: '100%' },
  footerLinks: {
    backgroundColor: '#353A40',
  },
  footerLegal: {
    backgroundColor: '#252A2E',
    width: '100%',
    padding: '2rem 0',
    margin: '0',
  },
  footerLegalLogo: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  footerLegalLogoText: {
    color: '#FFF',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '350',
    lineHeight: '37px',
  },
  footerLegalText: {
    color: '#FFF',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '350',
    lineHeight: '28px',
    marginTop: 0,
  },
  footerLegalLinks: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '350',
    '& a': {
      color: '#FFF',
      width: 'fit-content',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  topBarLogo: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
};

const TrimbleSiteLayout = (props: PropsWithChildren) => {
  const { children } = props;
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleLogout = async () => {
    await logout();
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Grid style={styles.root}>
      <Grid xs={12} item>
        <Grid component={'header'} style={styles.header} container>
          <Grid item xs={12} component={'nav'} style={styles.topBar}>
            <GridSpacingLayout>
              <div style={styles.topBarLogo}>
                <img src={trimbleLogo} alt='Trimble Logo' style={{ height: '25px' }} />
                <Avatar
                  alt={user?.name}
                  src={user?.picture}
                  sx={{ backgroundColor: 'primary.main', width: 48, height: 48, marginRight: 1 }}
                  id='user-avatar'
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClick}
                  style={{ cursor: 'pointer' }}
                />
                <Menu
                  id='basic-menu'
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button',
                  }}
                >
                  <MenuItem onClick={handleLogout}>
                    {t('TRIMBLE_LAYOUT.HEADER.LOGOUT', {
                      defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.HEADER.LOGOUT,
                    })}
                  </MenuItem>
                </Menu>
              </div>
            </GridSpacingLayout>
          </Grid>
          <Grid item xs={12} style={styles.subheader}>
            <GridSpacingLayout>
              {/* @ts-ignore */}
              <AccountNameText accountId={user?.account_id ?? ''} />
            </GridSpacingLayout>
          </Grid>
        </Grid>
      </Grid>
      <Grid xs={12} item className='app-body'>
        <main>{children}</main>
      </Grid>
      <Grid xs={12} item>
        <Grid item xs={12} style={styles.subheader}>
          <GridSpacingLayout>
            <Grid
              container
              item
              component={'footer'}
              style={styles.footerLegal}
              justifyContent={'space-between'}
              gap={2}
            >
              <Grid item xs={12} md={2}>
                <div style={styles.footerLegalLogo}>
                  <img
                    src={trimbleLogoWhite}
                    alt='Trimble Logo'
                    style={{ width: '141.56px', height: '32px' }}
                  />
                  <p style={styles.footerLegalLogoText}>
                    {t('TRIMBLE_LAYOUT.FOOTER.COPYRIGHT', {
                      defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.FOOTER.COPYRIGHT,
                    })}
                  </p>
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <p style={styles.footerLegalText}>
                  {t('TRIMBLE_LAYOUT.FOOTER.LEGAL_TEXT', {
                    defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.FOOTER.LEGAL_TEXT,
                  })}
                </p>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={styles.footerLegalLinks}>
                  <a
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    href={'https://www.trimble.com/en/legal'}
                  >
                    {t('TRIMBLE_LAYOUT.FOOTER.LEGAL_TERMS', {
                      defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.FOOTER.LEGAL_TERMS,
                    })}
                  </a>
                  <a
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    href={'https://www.trimble.com/en/legal/terms-and-conditions/terms-of-use'}
                  >
                    {t('TRIMBLE_LAYOUT.FOOTER.WEBSITE_TERMS', {
                      defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.FOOTER.WEBSITE_TERMS,
                    })}
                  </a>
                  <a
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    href={
                      'https://www.trimble.com/en/our-commitment/responsible-business/data-privacy-and-security/data-privacy-center'
                    }
                  >
                    {t('TRIMBLE_LAYOUT.FOOTER.DATA_PRIVACY', {
                      defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.FOOTER.DATA_PRIVACY,
                    })}
                  </a>
                  <a
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    href={
                      'https://www.trimble.com/en/our-commitment/responsible-business/data-privacy-and-security/data-privacy-center/privacy-notice'
                    }
                  >
                    {t('TRIMBLE_LAYOUT.FOOTER.PRIVACY_NOTICE', {
                      defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.FOOTER.PRIVACY_NOTICE,
                    })}
                  </a>
                  <a
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    href={
                      'https://www.trimble.com/en/our-commitment/responsible-business/data-privacy-and-security/data-privacy-center/ca-residents-notice-at-Collection'
                    }
                  >
                    {t('TRIMBLE_LAYOUT.FOOTER.CALIFORNIA_PRIVACY', {
                      defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.FOOTER.CALIFORNIA_PRIVACY,
                    })}
                  </a>
                  <a
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    href={
                      'https://www.trimble.com/en/our-commitment/responsible-business/data-privacy-and-security/data-privacy-center/your-privacy-choices'
                    }
                  >
                    {t('TRIMBLE_LAYOUT.FOOTER.PRIVACY_CHOICES', {
                      defaultValue: I18N_RESOURCE.TRIMBLE_LAYOUT.FOOTER.PRIVACY_CHOICES,
                    })}
                  </a>
                </Box>
              </Grid>
            </Grid>
          </GridSpacingLayout>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TrimbleSiteLayout;
